// src/pages/ContactPage.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    MapPin, Phone, Mail, Clock, Send, Building2, FileText,
    Instagram, Linkedin, Twitter, MessageSquare, User,
    ArrowLeft, CheckCircle, AlertCircle, Globe, ExternalLink
} from 'lucide-react';
import Header from '../components/Header';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { useSearch } from '../context/SearchContext';
import { openCartSidebar } from '../components/CartSidebar';
import { getAllProducts } from '../services/productService';
import Footer from '../components/Footer';
import jpglogo from '../assets/tronalogo.png';

export default function ContactPage() {
    const navigate = useNavigate();
    const [products, setProducts] = useState([]);
    const [availableCategories, setAvailableCategories] = useState([]);
    const [openFAQId, setOpenFAQId] = useState(null);

    const { cartCount } = useCart();
    const { wishlistCount } = useWishlist();
    const { searchQuery, setSearchQuery } = useSearch();

    React.useEffect(() => {
        fetchProducts();
    }, []);

    const fetchProducts = async () => {
        try {
            const result = await getAllProducts();
            if (result.success) {
                const activeProducts = result.products.filter(product =>
                    product.isActive !== false
                );
                setProducts(activeProducts);

                const categoriesSet = new Set();
                activeProducts.forEach(product => {
                    if (product.category) {
                        const mainCat = product.category.includes(' > ')
                            ? product.category.split(' > ')[0]
                            : product.category;
                        categoriesSet.add(mainCat);
                    }
                });
                setAvailableCategories(Array.from(categoriesSet));
            }
        } catch (error) {
            console.error('Ürünler yüklenemedi:', error);
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
        }
    };

    return (
        <div className="contact-page">
            <Style />

            <Header
                wishlistCount={wishlistCount}
                cartCount={cartCount}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                onSearch={handleSearch}
                onCartClick={openCartSidebar}
                onWishlistClick={() => navigate('/wishlist')}
                onCampaignsClick={() => navigate('/campaigns')}
                availableCategories={availableCategories}
                products={products}
            />

            {/* Hero Section */}
            <div className="contact-hero">
                <div className="container">
                    <div className="hero-content">
                        <h1>İletişim</h1>
                        <p>Bize ulaşın, size yardımcı olmaktan mutluluk duyarız</p>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="contact-content">
                <div className="container">
                    {/* Quick Contact Cards */}
                    <div className="quick-contact-grid">
                        <QuickContactCard
                            icon={<Phone size={28} />}
                            title="Telefon"
                            info="0535 643 41 81"
                            subinfo="Hafta içi 09:00 - 18:00"
                            gradient="linear-gradient(135deg, #E5332A, #ff4436)"
                        />
                        <QuickContactCard
                            icon={<Mail size={28} />}
                            title="E-posta"
                            info="info@tronapazarlama.com"
                            subinfo="7/24 destek"
                            gradient="linear-gradient(135deg, #06d6a0, #00b894)"
                        />
                        <QuickContactCard
                            icon={<MapPin size={28} />}
                            title="Adres"
                            info="Manavgat, Antalya, Türkiye"
                            subinfo="Merkez Ofis"
                            gradient="linear-gradient(135deg, #5f27cd, #341f97)"
                        />
                        <QuickContactCard
                            icon={<Clock size={28} />}
                            title="Çalışma Saatleri"
                            info="Pzt - Cum: 09:00 - 18:00"
                            subinfo="Cmt: 10:00 - 16:00"
                            gradient="linear-gradient(135deg, #FFB800, #FFA000)"
                        />
                    </div>

                    {/* Contact Methods Section */}
                    <div className="contact-methods-section">
                        <div className="section-header centered">
                            <h2>Bize Ulaşın</h2>
                            <p>Size en uygun iletişim yöntemini seçin</p>
                        </div>

                        <div className="methods-grid">
                            <ContactMethod
                                icon={<Mail size={32} />}
                                title="E-posta Gönderin"
                                description="info@tronapazarlama.com"
                                subtext="7/24 size dönüş yapıyoruz"
                                action="E-posta Gönder"
                                link="mailto:info@tronapazarlama.com"
                                gradient="linear-gradient(135deg, #06d6a0, #00b894)"
                            />
                            <ContactMethod
                                icon={<Phone size={32} />}
                                title="Bizi Arayın"
                                description="0535 643 41 81"
                                subtext="Hafta içi 09:00 - 18:00"
                                action="Hemen Ara"
                                link="tel: 0535 643 41 81"
                                gradient="linear-gradient(135deg, #E5332A, #ff4436)"
                            />
                            <ContactMethod
                                icon={<MessageSquare size={32} />}
                                title="WhatsApp Destek"
                                description="Hızlı cevap için"
                                subtext="Anında iletişim"
                                action="WhatsApp'tan Yaz"
                                link="https://wa.me/905356434181"
                                gradient="linear-gradient(135deg, #25D366, #128C7E)"
                            />
                        </div>
                    </div>

                    {/* Main Section: Company Info */}
                    <div className="company-section">
                        <div className="company-info-wrapper">
                            {/* Company Info */}
                            <div className="info-card">
                                <div className="info-header">
                                    <div className="company-logo">
                                        <img src={jpglogo} alt="Trona Pazarlama" />
                                    </div>
                                    <h3>Trona Pazarlama</h3>
                                </div>

                                <div className="info-divider"></div>

                                <div className="info-list">
                                    <InfoItem
                                        icon={<Building2 size={20} />}
                                        label="Firma Ünvanı"
                                        value="Trona Pazarlama Ltd. Şti."
                                    />
                                    <InfoItem
                                        icon={<FileText size={20} />}
                                        label="Vergi Dairesi"
                                        value="Manavgat Vergi Dairesi"
                                    />
                                    <InfoItem
                                        icon={<FileText size={20} />}
                                        label="Vergi No"
                                        value="8591116536"
                                    />
                                    <InfoItem
                                        icon={<FileText size={20} />}
                                        label="Ticaret Sicil No"
                                        value="12847"
                                    />
                                    <InfoItem
                                        icon={<FileText size={20} />}
                                        label="Mersis No"
                                        value="0859111653600001"
                                    />
                                    <InfoItem
                                        icon={<MapPin size={20} />}
                                        label="Adres"
                                        value="Manavgat, Antalya, Türkiye"
                                    />
                                    <InfoItem
                                        icon={<Phone size={20} />}
                                        label="Telefon"
                                        value="0535 643 41 81"
                                    />
                                    <InfoItem
                                        icon={<Mail size={20} />}
                                        label="E-posta"
                                        value="info@tronapazarlama.com"
                                    />
                                    <InfoItem
                                        icon={<Globe size={20} />}
                                        label="Website"
                                        value="https://tronapazarlama.com"
                                    />
                                </div>

                                <div className="info-divider"></div>

                                <div className="social-section">
                                    <h4>Sosyal Medya</h4>
                                    <div className="social-links">
                                        <a href="https://www.linkedin.com/in/%C3%B6mer-%C5%9Fahin-4a5355234/" target="_blank" rel="noreferrer" className="social-btn linkedin">
                                            <Linkedin size={20} />
                                        </a>
                                        <a href="https://instagram.com/tronapazarlama" target="_blank" rel="noreferrer" className="social-btn instagram">
                                            <Instagram size={20} />
                                        </a>
                                        <a href="#" className="social-btn twitter">
                                            <Twitter size={20} />
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Map Section - Full Width */}
                    <div className="map-section">
                        <div className="map-card-full">
                            <div className="map-header">
                                <MapPin size={24} />
                                <h3>Konumumuz</h3>
                            </div>
                            <div className="map-placeholder">
                                <iframe
                                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3192.0944799999997!2d31.4641367!3d36.7840989!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14c357bdc0501813%3A0x6e768aef2b4032b8!2sTrona%20Pazarlama%20Alkols%C3%BCz%20%C5%9Eampanya!5e0!3m2!1str!2str!4v1734276543210!5m2!1str!2str"
                                    width="100%"
                                    height="450"
                                    style={{ border: 0, borderRadius: '16px' }}
                                    allowFullScreen=""
                                    loading="lazy"
                                    referrerPolicy="no-referrer-when-downgrade"
                                    title="Trona Pazarlama Konum"
                                ></iframe>
                            </div>
                        </div>
                    </div>

                    {/* FAQ Section */}
                    <div className="faq-section">
                        <div className="section-header centered">
                            <h2>Sıkça Sorulan Sorular</h2>
                            <p>Merak ettiklerinizin cevapları burada</p>
                        </div>
                        <div className="faq-grid">
                            <FAQItem
                                id="faq-1"
                                question="Teslimat süresi ne kadar?"
                                answer="Siparişleriniz 1-3 iş günü içinde kargoya teslim edilir. Kargo teslimat süresi bölgeye göre 1-3 gün arasında değişmektedir."
                                isOpen={openFAQId === 'faq-1'}
                                onToggle={() => setOpenFAQId(openFAQId === 'faq-1' ? null : 'faq-1')}
                            />
                            <FAQItem
                                id="faq-2"
                                question="İade ve değişim koşulları nelerdir?"
                                answer="7 gün içinde ürünü iade edebilir veya değiştirebilirsiniz. Ürün kullanılmamış ve orijinal ambalajında olmalıdır."
                                isOpen={openFAQId === 'faq-2'}
                                onToggle={() => setOpenFAQId(openFAQId === 'faq-2' ? null : 'faq-2')}
                            />
                            <FAQItem
                                id="faq-3"
                                question="Kargo ücreti ne kadar?"
                                answer="Kargo ücreti ürüne göre farklılık göstermektedir."
                                isOpen={openFAQId === 'faq-3'}
                                onToggle={() => setOpenFAQId(openFAQId === 'faq-3' ? null : 'faq-3')}
                            />
                            <FAQItem
                                id="faq-4"
                                question="Hangi ödeme yöntemlerini kabul ediyorsunuz?"
                                answer="Kredi kartı ve banka kartı ile ödeme seçeneklerini sunuyoruz. Tüm ödemeler güvenli SSL ile korunmaktadır."
                                isOpen={openFAQId === 'faq-4'}
                                onToggle={() => setOpenFAQId(openFAQId === 'faq-4' ? null : 'faq-4')}
                            />
                            <FAQItem
                                id="faq-5"
                                question="Sipariş takibi nasıl yapılır?"
                                answer="Hesabınıza giriş yaparak 'Siparişlerim' bölümünden siparişlerinizi takip edebilirsiniz. Kargo takip numarası SMS ile gönderilir."
                                isOpen={openFAQId === 'faq-5'}
                                onToggle={() => setOpenFAQId(openFAQId === 'faq-5' ? null : 'faq-5')}
                            />
                            <FAQItem
                                id="faq-6"
                                question="Müşteri hizmetlerine nasıl ulaşabilirim?"
                                answer="Telefon, e-posta veya WhatsApp ile bize ulaşabilirsiniz. Hafta içi 09:00-18:00 arası hizmetinizdeyiz."
                                isOpen={openFAQId === 'faq-6'}
                                onToggle={() => setOpenFAQId(openFAQId === 'faq-6' ? null : 'faq-6')}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    );
}

// ==================== SUB COMPONENTS ====================

function QuickContactCard({ icon, title, info, subinfo, gradient }) {
    return (
        <div className="quick-contact-card">
            <div className="card-icon" style={{ background: gradient }}>
                {icon}
            </div>
            <h3>{title}</h3>
            <p className="main-info">{info}</p>
            <p className="sub-info">{subinfo}</p>
        </div>
    );
}

function ContactMethod({ icon, title, description, subtext, action, link, gradient }) {
    return (
        <div className="contact-method-card">
            <div className="method-icon" style={{ background: gradient }}>
                {icon}
            </div>
            <h3>{title}</h3>
            <p className="method-description">{description}</p>
            <p className="method-subtext">{subtext}</p>
            <a href={link} className="method-btn" style={{ background: gradient }}>
                {action}
                <ExternalLink size={18} />
            </a>
        </div>
    );
}

function InfoItem({ icon, label, value }) {
    return (
        <div className="info-item">
            <div className="info-icon">{icon}</div>
            <div className="info-text">
                <span className="info-label">{label}</span>
                <span className="info-value">{value}</span>
            </div>
        </div>
    );
}

function FAQItem({ id, question, answer, isOpen, onToggle }) {
    return (
        <div className={`faq-item ${isOpen ? 'open' : ''}`}>
            <button className="faq-question" onClick={onToggle}>
                <span>{question}</span>
                <div className={`faq-icon ${isOpen ? 'rotate' : ''}`}>
                    <CheckCircle size={20} />
                </div>
            </button>
            <div className={`faq-answer-wrapper ${isOpen ? 'open' : ''}`}>
                <div className="faq-answer">
                    <p>{answer}</p>
                </div>
            </div>
        </div>
    );
}

function Style() {
    return <style>{`
        /* ==================== GLOBAL STYLES ==================== */
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
            -webkit-tap-highlight-color: transparent;
        }

        html {
            scroll-behavior: smooth;
        }

        body {
            font-family: 'Inter', -apple-system, sans-serif;
            background: #f8f9fa;
            color: #2B2B2B;
        }

        img {
            max-width: 100%;
            height: auto;
        }

        /* ==================== CONTAINER ==================== */
        .container {
            max-width: 1320px;
            margin: 0 auto;
            padding: 0 20px;
        }

        /* ==================== PAGE WRAPPER ==================== */
        .contact-page {
            min-height: 100vh;
            background: #f8f9fa;
        }

        /* ==================== HERO SECTION ==================== */
        .contact-hero {
            background: linear-gradient(135deg, #2B2B2B 0%, #1a1a1a 100%);
            padding: 32px 0 64px;
            position: relative;
            overflow: hidden;
        }

        .contact-hero::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: 
                radial-gradient(circle at 20% 50%, rgba(229, 51, 42, 0.1), transparent 50%),
                radial-gradient(circle at 80% 80%, rgba(6, 214, 160, 0.08), transparent 50%);
            pointer-events: none;
        }

        .hero-content {
            text-align: center;
            color: #fff;
        }

        .hero-content h1 {
            font-size: 48px;
            font-weight: 800;
            margin-bottom: 12px;
            letter-spacing: -0.5px;
            padding-top: 32px;
        }

        .hero-content p {
            font-size: 18px;
            color: rgba(255, 255, 255, 0.7);
            padding-bottom: 32px;
        }

        /* ==================== CONTENT SECTION ==================== */
        .contact-content {
            padding: 40px 0 80px;
            margin-top: -48px;
            position: relative;
            z-index: 2;
        }

        /* ==================== QUICK CONTACT CARDS ==================== */
        .quick-contact-grid {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 20px;
            margin-bottom: 48px;
        }

        .quick-contact-card {
            background: #fff;
            border-radius: 16px;
            padding: 32px 24px;
            text-align: center;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
            transition: all 0.3s ease;
            border: 2px solid transparent;
        }

        .quick-contact-card:hover {
            transform: translateY(-4px);
            box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
            border-color: #E5332A;
        }

        .card-icon {
            width: 64px;
            height: 64px;
            border-radius: 16px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #fff;
            margin: 0 auto 16px;
        }

        .quick-contact-card h3 {
            font-size: 16px;
            font-weight: 700;
            color: #2B2B2B;
            margin-bottom: 8px;
        }

        .quick-contact-card .main-info {
            font-size: 15px;
            font-weight: 600;
            color: #495057;
            margin-bottom: 4px;
        }

        .quick-contact-card .sub-info {
            font-size: 13px;
            color: #868e96;
        }

        /* ==================== CONTACT METHODS SECTION ==================== */
        .contact-methods-section {
            margin-bottom: 56px;
        }

        .section-header {
            margin-bottom: 32px;
        }

        .section-header.centered {
            text-align: center;
        }

        .section-header h2 {
            font-size: 28px;
            font-weight: 800;
            color: #2B2B2B;
            margin-bottom: 8px;
        }

        .section-header p {
            font-size: 15px;
            color: #868e96;
        }

        .methods-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 24px;
        }

        .contact-method-card {
            background: #fff;
            border-radius: 20px;
            padding: 40px 32px;
            text-align: center;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
            transition: all 0.3s ease;
            border: 2px solid transparent;
            display: flex;
            flex-direction: column;
            align-items: center;
        }

        .contact-method-card:hover {
            transform: translateY(-6px);
            box-shadow: 0 12px 32px rgba(0, 0, 0, 0.12);
            border-color: #E5332A;
        }

        .method-icon {
            width: 80px;
            height: 80px;
            border-radius: 20px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #fff;
            margin-bottom: 24px;
        }

        .contact-method-card h3 {
            font-size: 20px;
            font-weight: 800;
            color: #2B2B2B;
            margin-bottom: 12px;
        }

        .method-description {
            font-size: 16px;
            font-weight: 600;
            color: #495057;
            margin-bottom: 6px;
        }

        .method-subtext {
            font-size: 14px;
            color: #868e96;
            margin-bottom: 24px;
        }

        .method-btn {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 14px 28px;
            border-radius: 12px;
            color: #fff;
            font-weight: 700;
            font-size: 15px;
            text-decoration: none;
            transition: all 0.3s;
        }

        .method-btn:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
        }

        /* ==================== COMPANY SECTION ==================== */
        .company-section {
            margin-bottom: 56px;
        }

        .company-info-wrapper {
            max-width: 800px;
            margin: 0 auto;
        }

        .info-card,
        .map-card {
            background: #fff;
            border-radius: 20px;
            padding: 32px;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
        }

        .info-header {
            text-align: center;
            margin-bottom: 24px;
        }

        .company-logo {
            width: 100px;
            height: 100px;
            margin: 0 auto 16px;
            border-radius: 50%;
            overflow: hidden;
            background: #f8f9fa;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 10px;
        }

        .company-logo img {
            width: 100%;
            height: 100%;
            object-fit: contain;
        }

        .info-header h3 {
            font-size: 24px;
            font-weight: 800;
            color: #2B2B2B;
        }

        .info-divider {
            height: 2px;
            background: linear-gradient(90deg, transparent, #e9ecef, transparent);
            margin: 24px 0;
        }

        .info-list {
            display: flex;
            flex-direction: column;
            gap: 16px;
        }

        .info-item {
            display: flex;
            align-items: flex-start;
            gap: 12px;
            padding: 12px;
            border-radius: 10px;
            transition: all 0.2s;
        }

        .info-item:hover {
            background: #f8f9fa;
        }

        .info-icon {
            width: 40px;
            height: 40px;
            background: linear-gradient(135deg, #E5332A, #ff4436);
            border-radius: 10px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #fff;
            flex-shrink: 0;
        }

        .info-text {
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 4px;
        }

        .info-label {
            font-size: 12px;
            color: #868e96;
            font-weight: 600;
        }

        .info-value {
            font-size: 14px;
            color: #2B2B2B;
            font-weight: 600;
        }

        .social-section {
            text-align: center;
        }

        .social-section h4 {
            font-size: 16px;
            font-weight: 700;
            color: #2B2B2B;
            margin-bottom: 16px;
        }

        .social-links {
            display: flex;
            gap: 12px;
            justify-content: center;
        }

        .social-btn {
            width: 48px;
            height: 48px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #fff;
            transition: all 0.3s;
            text-decoration: none;
        }

        .social-btn.linkedin {
            background: linear-gradient(135deg, #0077B5, #005582);
        }

        .social-btn.instagram {
            background: linear-gradient(135deg, #E4405F, #C13584);
        }

        .social-btn.twitter {
            background: linear-gradient(135deg, #1DA1F2, #0d8bd9);
        }

        .social-btn:hover {
            transform: translateY(-4px);
            box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
        }

        /* ==================== MAP CARD ==================== */
        .map-section {
            margin-bottom: 56px;
        }

        .map-card-full {
            background: #fff;
            border-radius: 20px;
            padding: 32px;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
        }

        .map-card-full .map-header {
            display: flex;
            align-items: center;
            gap: 12px;
            margin-bottom: 24px;
            color: #2B2B2B;
        }

        .map-card-full .map-header h3 {
            font-size: 24px;
            font-weight: 800;
        }

        .map-header {
            display: flex;
            align-items: center;
            gap: 10px;
            margin-bottom: 16px;
            color: #2B2B2B;
        }

        .map-header h4 {
            font-size: 18px;
            font-weight: 700;
        }

        .map-placeholder {
            border-radius: 12px;
            overflow: hidden;
        }

        .map-card-full .map-placeholder {
            border-radius: 16px;
            overflow: hidden;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
        }

        /* ==================== FAQ SECTION ==================== */
        .faq-section {
            margin-top: 56px;
        }

        .faq-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 20px;
            align-items: start;
        }

        .faq-item {
            background: #fff;
            border-radius: 16px;
            overflow: hidden;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
            transition: box-shadow 0.3s;
            height: fit-content;
        }

        .faq-item:hover {
            box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
        }

        .faq-question {
            width: 100%;
            background: none;
            border: none;
            padding: 20px 24px;
            text-align: left;
            cursor: pointer;
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 16px;
            font-weight: 600;
            font-size: 15px;
            color: #2B2B2B;
            transition: all 0.2s;
        }

        .faq-question:hover {
            background: #f8f9fa;
        }

        .faq-icon {
            color: #868e96;
            transition: all 0.3s;
            flex-shrink: 0;
        }

        .faq-icon.rotate {
            transform: rotate(180deg);
            color: #E5332A;
        }

        .faq-answer-wrapper {
            max-height: 0;
            overflow: hidden;
            transition: max-height 0.4s ease-out;
        }

        .faq-answer-wrapper.open {
            max-height: 1000px;
            transition: max-height 0.4s ease-in;
        }

        .faq-answer {
            margin-top: 8px;
            padding: 0 24px 20px;
        }

        .faq-answer p {
            color: #495057;
            line-height: 1.7;
            font-size: 14px;
        }

        /* ==================== RESPONSIVE ==================== */
        @media (max-width: 1200px) {
            .quick-contact-grid {
                grid-template-columns: repeat(2, 1fr);
            }

            .company-info-wrapper {
                max-width: 100%;
            }
        }

        @media (max-width: 992px) {
            .methods-grid {
                grid-template-columns: 1fr;
            }

            .faq-grid {
                grid-template-columns: 1fr;
            }
        }

        @media (max-width: 768px) {
            .contact-hero {
                padding: 20px 0 48px;
            }

            .hero-content h1 {
                font-size: 32px;
            }

            .quick-contact-grid {
                grid-template-columns: 1fr;
            }

            .contact-method-card {
                padding: 32px 24px;
            }

            .map-card-full {
                padding: 24px;
            }
        }

        @media (max-width: 480px) {
            .hero-content h1 {
                font-size: 24px;
            }

            .info-card,
            .map-card-full {
                padding: 20px;
            }

            .contact-method-card {
                padding: 24px 20px;
            }
        }
    `}</style>;
}